import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import './Rewards.css';

interface Reward {
  id: string;
  name: string;
  description: string;
  pointsRequired: number;
  category: 'food' | 'merchandise';
  imageUrl: string;
  available: boolean;
}

const rewards: Reward[] = [
  {
    id: '1',
    name: 'Samosa',
    description: 'One fresh hot samosa from the canteen',
    pointsRequired: 50,
    category: 'food',
    imageUrl: '🥟',
    available: true,
  },
  {
    id: '2',
    name: 'Coffee/Tea',
    description: 'One cup of coffee or tea of your choice',
    pointsRequired: 100,
    category: 'food',
    imageUrl: '☕',
    available: true,
  },
  {
    id: '3',
    name: 'Fries',
    description: 'Crispy golden fries',
    pointsRequired: 150,
    category: 'food',
    imageUrl: '🍟',
    available: true,
  },
  {
    id: '4',
    name: 'Shawarma',
    description: 'Delicious chicken or veg shawarma',
    pointsRequired: 350,
    category: 'food',
    imageUrl: '🌯',
    available: true,
  },
  {
    id: '5',
    name: 'Biryani/Chowmein',
    description: 'Choice of biryani or chowmein',
    pointsRequired: 400,
    category: 'food',
    imageUrl: '🍜',
    available: true,
  },
  {
    id: '6',
    name: 'Certificate of Recognition',
    description: 'Official certificate for your environmental efforts',
    pointsRequired: 800,
    category: 'merchandise',
    imageUrl: '📜',
    available: true,
  },
  {
    id: '7',
    name: 'Campus T-Shirt/Hoodie',
    description: 'Exclusive campus cleanliness champion merchandise',
    pointsRequired: 1000,
    category: 'merchandise',
    imageUrl: '👕',
    available: true,
  },
];

interface RewardsProps {
  onNavigate: (page: string) => void;
  showToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

const Rewards: React.FC<RewardsProps> = ({ onNavigate, showToast }) => {
  const { user, updateUser } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showClaimModal, setShowClaimModal] = useState<Reward | null>(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [myRedemptions, setMyRedemptions] = useState<any[]>([]);
  const [showMyRedemptions, setShowMyRedemptions] = useState(false);

  useEffect(() => {
    if (user) {
      loadMyRedemptions();
    }
  }, [user]);

  const loadMyRedemptions = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('reward_redemptions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setMyRedemptions(data);
    }
  };

  const categories = ['all', 'food', 'merchandise'];

  const filteredRewards = rewards.filter(
    (reward) => selectedCategory === 'all' || reward.category === selectedCategory
  );

  const canClaim = (reward: Reward) => {
    return user && user.points >= reward.pointsRequired && reward.available;
  };

  const handleClaim = (reward: Reward) => {
    if (canClaim(reward)) {
      setShowClaimModal(reward);
      setWhatsappNumber('');
    }
  };

  const confirmClaim = async () => {
    if (!showClaimModal || !user) {
      return;
    }
    
    // Validate WhatsApp number
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!whatsappNumber || !phoneRegex.test(whatsappNumber) || whatsappNumber.length < 10) {
      if (showToast) {
        showToast('Please enter a valid WhatsApp number', 'error');
      }
      return;
    }


    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('claim_reward', {
        p_reward_name: showClaimModal.name,
        p_points_cost: showClaimModal.pointsRequired,
        p_whatsapp_number: whatsappNumber
      });


      if (error) throw error;

      if (data && data.success) {
        // Update local user points
        updateUser({ points: data.new_points });
        
        if (showToast) {
          showToast('Reward claimed successfully! The admin will contact you soon.', 'success');
        }
        
        setShowClaimModal(null);
        loadMyRedemptions();
      } else {
        if (showToast) {
          showToast(data?.error || 'Failed to claim reward', 'error');
        }
      }
    } catch (error: any) {
      if (showToast) {
        showToast(error.message || 'Failed to claim reward. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      'pending': { text: 'Pending', class: 'status-pending' },
      'approved': { text: 'Approved', class: 'status-approved' },
      'delivered': { text: 'Delivered', class: 'status-delivered' },
      'rejected': { text: 'Rejected', class: 'status-rejected' }
    };
    return badges[status] || badges['pending'];
  };

  return (
    <div className="rewards-container">
      <div className="rewards-header">
        <button onClick={() => onNavigate('dashboard')} className="back-button">
          ← Back
        </button>
        <h1>Rewards Store</h1>
        <p>Exchange your points for amazing rewards!</p>
        {user && (
          <div className="points-balance">
            <span>Your Points:</span>
            <span className="points-value">{user.points}</span>
          </div>
        )}
      </div>

      <div className="rewards-actions">
        <button 
          className="my-redemptions-btn"
          onClick={() => setShowMyRedemptions(!showMyRedemptions)}
        >
          My Redemptions ({myRedemptions.length})
        </button>
      </div>

      {showMyRedemptions && (
        <div className="my-redemptions">
          <h3>My Redemption History</h3>
          {myRedemptions.length === 0 ? (
            <p className="empty-state">You haven't claimed any rewards yet.</p>
          ) : (
            <div className="redemptions-list">
              {myRedemptions.map((redemption) => (
                <div key={redemption.id} className="redemption-item">
                  <div className="redemption-info">
                    <h4>{redemption.reward_name}</h4>
                    <p className="redemption-details">
                      {redemption.points_cost} points • WhatsApp: {redemption.whatsapp_number}
                    </p>
                    <p className="redemption-date">
                      {new Date(redemption.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className={`redemption-status ${getStatusBadge(redemption.status).class}`}>
                    {getStatusBadge(redemption.status).text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="category-filters">
        {categories.map((category) => (
          <button
            key={category}
            className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      <div className="rewards-grid">
        {filteredRewards.map((reward) => (
          <div key={reward.id} className="reward-card">
            <div className="reward-image">{reward.imageUrl}</div>
            <div className="reward-content">
              <h3>{reward.name}</h3>
              <p className="reward-description">{reward.description}</p>
              <div className="reward-footer">
                <div className="points-required">
                  <span className="points-label">Required:</span>
                  <span className="points-amount">{reward.pointsRequired} pts</span>
                </div>
                <button
                  className={`claim-btn ${canClaim(reward) ? 'available' : 'unavailable'}`}
                  onClick={() => handleClaim(reward)}
                  disabled={!canClaim(reward)}
                >
                  {!user ? 'Login to Claim' : 
                   !reward.available ? 'Out of Stock' :
                   user.points < reward.pointsRequired ? 'Insufficient Points' : 
                   'Claim Reward'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showClaimModal && (
        <div className="modal-overlay" onClick={() => setShowClaimModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Claim Reward</h2>
            <div className="modal-reward">
              <div className="modal-image">{showClaimModal.imageUrl}</div>
              <h3>{showClaimModal.name}</h3>
              <p>{showClaimModal.description}</p>
            </div>
            
            <div className="whatsapp-input-section">
              <label htmlFor="whatsapp">WhatsApp Number *</label>
              <input
                type="tel"
                id="whatsapp"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="Enter your WhatsApp number"
                className="whatsapp-input"
                disabled={isSubmitting}
              />
              <p className="input-hint">Admin will contact you on this number for delivery</p>
            </div>

            <div className="modal-info">
              <p>This will deduct <strong>{showClaimModal.pointsRequired} points</strong> from your balance.</p>
              <p>Remaining points: <strong>{user!.points - showClaimModal.pointsRequired}</strong></p>
            </div>
            
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setShowClaimModal(null)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn" 
                onClick={confirmClaim}
                disabled={isSubmitting || !whatsappNumber}
              >
                {isSubmitting ? 'Processing...' : 'Confirm Claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rewards;